<template>
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />

  <div v-else class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Modules - {{ filteredModules.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
    </div>

    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search modules by name..."
        />
      </div>

      <!-- Bulk Action Section -->
      <div class="flex items-center mt-4 md:mt-0 space-x-2">
        <GlassSelect
          v-model="bulkAction"
          :options="[
            { value: 'enable',  label: 'Enable selected'      },
            { value: 'disable', label: 'Disable selected'      },
            { value: 'delete',  label: 'Delete selected items' },
          ]"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedModules.length === 0"
          :class="[
            'px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200',
            bulkAction && selectedModules.length
              ? bulkAction === 'delete' ? 'bg-red-600 text-white hover:bg-red-700'
              :                          'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- Modules Table -->
    <GenericTable
      ref="table"
      :columns="columns"
      :items="paginatedModules"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedModules"
      @rowClick="goToModuleDetail"
    >
      <!-- Custom cell templates -->
      <template #enabled="{ value }">
        {{ value ? "Yes" : "No" }}
      </template>
      <template #combinedName="{ item }">
        {{ item.combinedName }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
          :valueKey="'value'"
          :labelKey="'label'"
          placeholder="Items per page"
          @change="updatePagination"
        />
      </div>
    </div>
    <DeleteModal
      v-if="showBulkConfirmModal"
      :action="pendingBulkAction"
      :componet-objects="selectedModuleNames"
      :count="selectedModuleSummary"
      :is-child-object="true"
      @delete="confirmAction"
      @close="showBulkConfirmModal=false"
    />
  </div>
</template>

<script>
import { getAllModules, deleteModule, updateModule } from "@/services/moduleService";
import { getAllOrgs } from "@/services/orgService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import DeleteModal from "@/components/DeleteModal.vue";

export default {
  name: "ModuleView",
  components: {
    SearchBar,
    GenericTable,
    PageError,
    GlassSelect,
    DeleteModal,
  },
  data() {
    return {
      modules: [],
      organizations: {},
      searchQuery: "",
      filteredModules: [],
      currentPage: 1,
      itemsPerPage: 20,
      selectedModules: [],
      showBulkConfirmModal: false,
      bulkAction: "",
      pendingBulkAction: "",
      columns: [
        { key: "combinedName", label: "Name", sortable: true, width: "60%" },
        { key: "enabled", label: "Enabled", sortable: true, width: "20%" },
        { key: "uiconfig", label: "UI Config", sortable: true, width: "20%" },
      ],
      errorMessages: {
        400 : "Bad Request .The server could not understand the request.",
        401 : "Unauthorized access. Please check your authentication token or log in again.",
        403 : "You do not have permission to access this resource. Contact a staff member to assign you the permissions to access this page.",
        404 : "Page Not Found.",
        500 : "Internal Server Error - Something went wrong on the server."
      },
      error: ""
    };
  },
  computed: {
    isFiltered() {
      return this.searchQuery !== "";
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredModules.length / this.itemsPerPage);
    },
    paginatedModules() {
      if (this.itemsPerPage === -1) {
        return this.filteredModules;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredModules.slice(start, end);
    },
    selectedModuleNames() {
      return this.selectedModules.map(module => ({
        name: "Module",
        value: module.combinedName,
      }));
    },
    selectedModuleSummary() {
      return [{ name: "Modules", value: this.selectedModuleNames.length }];
    }
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
  },
  mounted() {
    this.fetchOrganizations().then(() => this.fetchModules());
  },
  methods: {
    async fetchOrganizations() {
      try {
        const orgs = await getAllOrgs();
        this.organizations = orgs.reduce((acc, org, index) => {
          acc[org.id] = org.name;
          return acc;
        }, {});
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    },
    async fetchModules() {
      try {
        const modules = await getAllModules();
        this.modules = modules.map((module) => ({
          ...module,
          combinedName: `${
            module.name.toLowerCase() === "blackbox"
              ? "Endpoint Availability"
              : module.name.toLowerCase() === "hostavailability"
              ? "Host Availability"
              : module.name
          }-${this.organizations[module.uiconfig] || "Unknown"}-config`,
        }));
        this.applyFilters();
      } catch (error) {
        console.error("Error fetching modules:", error);
        if (error.response) {
          this.error = `${this.errorMessages[error.response.status]}`;
          this.errorCode = `${error.response.status}`;
        } else{
          this.error = `Unknown Error`
        }
      
        // Handle error (e.g., show error message to user)
      }
    },
    goToModuleDetail(module) {
      this.$router.push({
        name: "ModuleDetails",
        params: { id: module.id },
      });
    },
    updateSelectedModules(selectedItems) {
      this.selectedModules = selectedItems;
    },
    applyBulkAction() {
      if (this.selectedModules.length === 0) return;
      this.pendingBulkAction = this.bulkAction;
      this.showBulkConfirmModal = true;
      this.bulkAction = "";
    },
    async confirmAction() {
      if (this.pendingBulkAction === "delete")  await this.deleteSelectedModules();
      if (this.pendingBulkAction === "enable")  await this.enableSelectedModules(true);
      if (this.pendingBulkAction === "disable") await this.enableSelectedModules(false);
      this.showBulkConfirmModal = false;
      this.pendingBulkAction = "";
    },
    async deleteSelectedModules() {
      if (this.selectedModules.length === 0) {
        alert("No modules selected for deletion");
        return;
      }
      
      try {
        const deletePromises = this.selectedModules.map((moduleItem) =>
          deleteModule(moduleItem.id)
        );
        await Promise.all(deletePromises);

        this.modules = this.modules.filter(
          (moduleItem) => !this.selectedModules.some((item) => item.id === moduleItem.id)
        );
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedModules = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert("An error occurred while deleting modules. Please try again.");
      }
    },
    async enableSelectedModules(enabled) {
      try {
        await Promise.all(
          this.selectedModules.map((module) =>
            updateModule(module.id, {
              name: module.name.toLowerCase() === "blackbox" ? "Blackbox" : module.name,
              enabled,
              uiconfig: module.uiconfig,
            })
          )
        );
        await this.fetchModules();
        this.$refs.table.clearSelection();
        this.selectedModules = [];
      } catch (error) {
        console.error("Error during bulk update:", error);
        alert("An error occurred while updating modules. Please try again.");
      }
    },
    applyFilters() {
      this.filteredModules = this.modules.filter(
        (moduleItem) =>
          moduleItem.combinedName
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase()) && moduleItem.uiconfig !== 1
      );
      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
  },
};
</script>
